import { useEffect, useMemo, useRef, useState } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { Button } from '@/components/ui'
import { Button as ShadButton } from '@/components/shadcn/button'
import { Textarea } from '@/components/shadcn/textarea'
import { ask } from '@/components/ask'
import type { FilesClient } from '@/lib/files'

const MAX_EDIT = 5 * 1024 * 1024
const MAX_PREVIEW = 50 * 1024 * 1024
const IMAGE_TYPES: Record<string, string> = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', gif: 'image/gif', webp: 'image/webp', svg: 'image/svg+xml', avif: 'image/avif', bmp: 'image/bmp', ico: 'image/x-icon',
}
const PDF = 'application/pdf'

/** Images and PDFs are shown, not edited. */
function previewType(path: string): string | undefined {
  const ext = path.slice(path.lastIndexOf('.') + 1).toLowerCase()
  return ext === 'pdf' ? PDF : IMAGE_TYPES[ext]
}

/**
 * A plain editor for one file of a session: line numbers and a Save button,
 * nothing else. It saves through the file panel's socket; the box refuses a
 * save when the file changed since it was opened, so a person never silently
 * writes over what Claude just did.
 */
export function FileEditor({ client, path, bytes, mtime, writable, onClose }: {
  client: FilesClient
  path: string
  bytes: number
  mtime: number
  writable: boolean
  onClose: () => void
}) {
  const [text, setText] = useState<string | null>(null)
  const [saved, setSaved] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const at = useRef(mtime)
  const area = useRef<HTMLTextAreaElement>(null)
  const gutter = useRef<HTMLDivElement>(null)
  const preview = previewType(path)
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!preview) return
    if (bytes > MAX_PREVIEW) { setError('This file is over 50 MB; download it instead.'); return }
    let u: string | null = null
    client.download(path).then((blob) => { u = URL.createObjectURL(new Blob([blob], { type: preview })); setUrl(u) })
      .catch((e: Error) => setError(e.message))
    return () => { if (u) URL.revokeObjectURL(u) }
  }, [client, path, bytes, preview])

  useEffect(() => {
    if (preview) return
    if (bytes > MAX_EDIT) { setError('This file is over 5 MB; download it instead.'); return }
    client.download(path).then(async (blob) => {
      const buf = new Uint8Array(await blob.arrayBuffer())
      if (buf.includes(0)) { setError('This is not a text file; download it instead.'); return }
      const t = new TextDecoder().decode(buf)
      setText(t); setSaved(t)
    }).catch((e: Error) => setError(e.message))
  }, [client, path, bytes, preview])

  const dirty = text !== null && text !== saved
  const lines = useMemo(() => (text ?? '').split('\n').length, [text])

  const save = async () => {
    if (!writable || text === null || saving) return
    setSaving(true); setError(null)
    try {
      const next = await client.save(path, text, at.current)
      if (next !== undefined) at.current = next
      setSaved(text)
    } catch (e) { setError((e as Error).message) } finally { setSaving(false) }
  }

  const close = async () => {
    if (dirty && !(await ask({ title: 'Close without saving?', body: 'Your changes to this file are lost.', confirm: 'Close', danger: true }))) return
    onClose()
  }

  const keys = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 's') { e.preventDefault(); void save(); return }
    if (e.key === 'Tab' && writable) {
      e.preventDefault()
      e.currentTarget.setRangeText('  ', e.currentTarget.selectionStart, e.currentTarget.selectionEnd, 'end')
      setText(e.currentTarget.value)
    }
  }

  return (
    <DialogPrimitive.Root open onOpenChange={(o) => { if (!o) void close() }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/60" />
        <DialogPrimitive.Content className="fixed inset-[4vh_4vw] z-50 flex flex-col rounded-lg border border-border-strong bg-surface-3 focus:outline-none shadow-lg"
          onEscapeKeyDown={(e) => { e.preventDefault(); void close() }}>
          <div className="flex h-10 shrink-0 items-center gap-2 border-b border-border px-3">
            <DialogPrimitive.Title className="min-w-0 truncate font-mono text-xs">{path}</DialogPrimitive.Title>
            <DialogPrimitive.Description className="sr-only">Edit {path}</DialogPrimitive.Description>
            {dirty && <span className="text-[10px] text-warn">unsaved</span>}
            {!writable && <span className="text-[10px] text-fg-faint">read only</span>}
            <span className="ml-auto" />
            {writable && !preview && <Button size="sm" variant="primary" disabled={!dirty || saving} onClick={() => void save()}>{saving ? 'Saving…' : 'Save'}</Button>}
            <ShadButton type="button" variant="ghost" size="icon-xs" aria-label="Close" className="rounded-xs text-fg-subtle hover:bg-hover" onClick={() => void close()}><X size={14} /></ShadButton>
          </div>
          {error && <div role="alert" className="border-b border-border px-3 py-1 text-xs text-err">{error}</div>}
          {preview ? (
            url ? (preview === PDF
              ? <iframe title={path} src={url} className="min-h-0 flex-1 bg-white" />
              : <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-[repeating-conic-gradient(#1a1a1c_0%_25%,#111113_0%_50%)] bg-[length:16px_16px] p-4">
                  <img src={url} alt={path} className="max-h-full max-w-full object-contain" />
                </div>)
            : <div className="p-3 text-xs text-fg-faint">{error ? '' : 'Loading…'}</div>
          ) : text === null ? <div className="p-3 text-xs text-fg-faint">{error ? '' : 'Loading…'}</div> : (
            <div className="flex min-h-0 flex-1 font-mono text-xs leading-5">
              <div ref={gutter} aria-hidden className="shrink-0 select-none overflow-hidden border-r border-border bg-surface-2 py-2 pr-2 pl-3 text-right text-fg-faint">
                {Array.from({ length: lines }, (_, i) => <div key={i}>{i + 1}</div>)}
              </div>
              <Textarea ref={area} value={text} readOnly={!writable} spellCheck={false} wrap="off" autoFocus
                aria-label={`Contents of ${path}`}
                className="field-sizing-fixed min-h-0 min-w-0 flex-1 resize-none rounded-none border-0 bg-transparent p-2 text-xs leading-5 whitespace-pre text-fg shadow-none focus-visible:ring-0 md:text-xs dark:bg-transparent"
                onChange={(e) => setText(e.target.value)} onKeyDown={keys}
                onScroll={(e) => { if (gutter.current) gutter.current.scrollTop = e.currentTarget.scrollTop }} />
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
